import { useCallback, useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'

const normalizeRanges = media => {
	if (!media) return null

	const list = Array.isArray(media) ? media : [media]

	const ranges = list.filter(Boolean).map(r => {
		const from = typeof r.from === 'number' ? r.from : 0
		const to = typeof r.to === 'number' ? r.to : Infinity
		return { from, to }
	})

	return ranges.length ? ranges : null
}

const buildQuery = ranges => {
	return ranges
		.map(r => {
			const min = r.from
			const max = Number.isFinite(r.to) ? r.to - 1 : null
			if (max == null) return `(min-width: ${min}px)`
			if (min <= 0) return `(max-width: ${max}px)`
			return `(min-width: ${min}px) and (max-width: ${max}px)`
		})
		.join(', ')
}

export const useListsTabs = ({
	tabs = [],
	activeTab,
	onSelect,
	showDropdownMedia,
}) => {
	const getCount = useCallback(
		id => {
			const found = tabs.find(t => String(t.id) === String(id))
			const value = found?.count
			return typeof value === 'number' ? value : undefined
		},
		[tabs]
	)

	const selectOptions = useMemo(() => {
		return tabs.map(tab => {
			const value = getCount(tab.id)
			const label =
				typeof value === 'number' ? `${tab.label} (${value})` : tab.label
			return { value: tab.id, label }
		})
	}, [tabs, getCount])

	const selectedOption = useMemo(() => {
		if (!selectOptions.length) return null
		const found = selectOptions.find(o => String(o.value) === String(activeTab))
		return found || selectOptions[0]
	}, [selectOptions, activeTab])

	const handleSelect = useCallback(
		id => {
			if (!onSelect) return
			onSelect(id)
		},
		[onSelect]
	)

	const handleSelectChange = useCallback(
		opt => {
			if (!opt) return
			handleSelect(opt.value)
		},
		[handleSelect]
	)

	const ranges = useMemo(
		() => normalizeRanges(showDropdownMedia),
		[showDropdownMedia]
	)

	const customQuery = useMemo(() => {
		if (!ranges) return null
		return buildQuery(ranges)
	}, [ranges])

	const isShowDropdownCustom = useMediaQuery({
		query: customQuery || 'not all',
	})

	const isShowDropdownDefault = useMediaQuery({
		query: '(max-width: 767px)',
	})

	const isShowDropdown = ranges ? isShowDropdownCustom : isShowDropdownDefault

	return {
		getCount,
		selectOptions,
		selectedOption,
		handleSelect,
		handleSelectChange,
		isShowDropdown,
	}
}
